/**
 * Canonical catalog of every New Relic custom event `name` emitted by
 * Reader / Bookwise / shared code. ALL call sites to `newRelicEvent(...)`
 * must pass one of these enum values — never a string literal.
 *
 * Values intentionally equal their dotted namespace strings so existing
 * NRQL dashboards (`FROM BookwiseEvent WHERE name = 'ui.tap'`) keep
 * working unchanged.
 *
 * When adding a new event:
 *   1. Add the enum member here.
 *   2. Add its payload type to `NrEventPayloads` (see events.ts) so the
 *      emit helper enforces a schema at the type level.
 */
export enum NrEvent {
  // ui.* — user-facing interaction + impression telemetry
  UiTap = 'ui.tap',
  UiItemViewed = 'ui.item-viewed',
  UiScreenView = 'ui.screen-view',
  UiSheetOpened = 'ui.sheet-opened',
  UiSheetClosed = 'ui.sheet-closed',

  ReaderScreenTap = 'reader.screen-tap',
  ReaderScreenSwipe = 'reader.screen-swipe',
  ReaderTap = 'reader.tap',
  ReaderScrollEnd = 'reader.scroll-end',
  ReaderChunkLoaded = 'reader.chunk-loaded',

  // api.* — outbound HTTP requests
  ApiRequest = 'api.request',

  // lifecycle.* — app foreground / background transitions
  LifecycleAppLaunched = 'lifecycle.app-launched',
  LifecycleAppBackgrounded = 'lifecycle.app-backgrounded',
  LifecycleAppForegrounded = 'lifecycle.app-foregrounded',

  // notification.* — push notification delivery + permission state
  NotificationOpened = 'notification.opened',
  NotificationPermissionGranted = 'notification.permission-granted',
  NotificationPermissionRevoked = 'notification.permission-revoked',

  // stateUpdate — single event for every UserEvent fan-out from the
  // state-update pipeline. The specific user event name is carried as
  // a mandatory `eventName` field on the payload (see NrEventPayloads)
  // so NRQL facets on one event kind rather than an unbounded set of
  // `action.*` event names.
  StateUpdate = 'stateUpdate',

  // goodreads.* — Goodreads-import telemetry; order a run's events by `runId`
  // (lifecycle/fetch-page) or timestamp to replay the journey.
  GoodreadsFetchPage = 'goodreads.fetch-page',
  GoodreadsImportStateChange = 'goodreads.import-state-change',
  GoodreadsImportLifecycle = 'goodreads.import-lifecycle',

  // widget.* — home screen widget timeline diagnostics, written by the iOS
  // widget extension into the shared app group and forwarded on foreground.
  // Facet on the `outcome` field ('ok' / 'stalled' / 'watchdog_timeout' / …).
  WidgetTimelineRun = 'widget.timeline-run',
}
